import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Pane } from "tweakpane";

// Debug
const pane = new Pane({
  title: "Debug",
  expanded: true,
});
pane.element.style.display = "none";
window.addEventListener("keydown", (event) => {
  if (event.key === "d") {
    if (pane.element.style.display === "none") {
      pane.element.style.display = "block";
    } else {
      pane.element.style.display = "none";
    }
  }
  if (event.key === "c") {
    console.log(camera.position);
  }
});

// Loaders
const gltfLoader = new GLTFLoader();

// Canvas
const canvas = document.getElementById("webgl");

// Scene
const scene = new THREE.Scene();
scene.background = new THREE.Color(0xc7c7c7);

// Materials
const material = new THREE.MeshStandardMaterial({
  color: 0xeed9c4,
});
const hoverMaterial = new THREE.MeshStandardMaterial({
  color: 0xd9b99b,
});
const selectedMaterial = new THREE.MeshStandardMaterial({
  color: 0xcda27b,
});

// Model
const hoverObjectNames = [
  "Frontal_Lobe",
  "Parietal_Lobe",
  "Occipital_Lobe",
  "Temporal_Lobe",
  "Cerebellum",
];

// Region data
const regionData = {
  Frontal_Lobe: {
    name: "Frontal Lobe",
    subtitle: "Executive Functions & Motor Control",
    description:
      "The frontal lobe orchestrates executive functions including working memory, cognitive flexibility, and inhibitory control. Contains the primary motor cortex (Brodmann area 4) and premotor areas that coordinate voluntary movement. Broca's area (BA 44/45) enables speech production, while the prefrontal cortex mediates decision-making and social cognition.",
    functions: [
      "Executive Control",
      "Motor Planning",
      "Speech Production",
      "Social Behavior",
    ],
    keyAreas: ["Primary Motor Cortex", "Broca's Area", "Prefrontal Cortex"],
    references: ["Fuster, 2015", "Miller & Cohen, 2001"],
    cameraPosition: { x: 3.437, y: 1.5, z: 2.4 },
  },
  Parietal_Lobe: {
    name: "Parietal Lobe",
    subtitle: "Sensory Integration & Spatial Processing",
    description:
      "Integrates multisensory information through the primary somatosensory cortex (BA 1-3) and association areas. Critical for spatial attention, body schema representation, and sensorimotor coordination. The intraparietal sulcus processes numerical cognition and spatial working memory.",
    functions: [
      "Sensory Integration",
      "Spatial Attention",
      "Body Schema",
      "Numerical Processing",
    ],
    keyAreas: [
      "Primary Somatosensory Cortex",
      "Intraparietal Sulcus",
      "Superior Parietal Lobule",
    ],
    references: ["Andersen et al., 1997", "Dehaene et al., 2003"],
    cameraPosition: { x: -2.749, y: 2.609, z: 2.329 },
  },
  Occipital_Lobe: {
    name: "Occipital Lobe",
    subtitle: "Visual Processing Hub",
    description:
      "Contains the primary visual cortex (V1, BA 17) and higher-order visual areas (V2-V5) that process visual information hierarchically. V1 extracts basic features (orientation, color, motion), while extrastriate areas perform complex pattern recognition and object identification.",
    functions: [
      "Visual Perception",
      "Feature Detection",
      "Object Recognition",
      "Motion Processing",
    ],
    keyAreas: [
      "Primary Visual Cortex (V1)",
      "Extrastriate Areas (V2-V5)",
      "Visual Association Cortex",
    ],
    references: ["Hubel & Wiesel, 1962", "Zeki, 1993"],
    cameraPosition: { x: -4.355, y: 0.169, z: 0.895 },
  },
  Temporal_Lobe: {
    name: "Temporal Lobe",
    subtitle: "Auditory Processing & Memory Formation",
    description:
      "Processes auditory information through the primary auditory cortex (BA 41/42) and association areas. Houses Wernicke's area (BA 22) for language comprehension and the medial temporal lobe structures (hippocampus, amygdala) essential for memory formation and emotional processing.",
    functions: [
      "Auditory Processing",
      "Language Comprehension",
      "Memory Formation",
      "Emotional Processing",
    ],
    keyAreas: [
      "Primary Auditory Cortex",
      "Wernicke's Area",
      "Hippocampus",
      "Amygdala",
    ],
    references: ["Squire & Zola-Morgan, 1991", "LeDoux, 2000"],
    cameraPosition: { x: 0, y: 0, z: 4.42 },
  },
  Cerebellum: {
    name: "Cerebellum",
    subtitle: "Motor Coordination & Learning",
    description:
      "Fine-tunes motor commands through the cerebellar cortex and deep nuclei. Essential for motor learning, timing, and coordination of complex movements. Recent research reveals its role in cognitive functions including language, working memory, and emotional regulation through cerebro-cerebellar loops.",
    functions: [
      "Motor Coordination",
      "Motor Learning",
      "Timing",
      "Cognitive Functions",
    ],
    keyAreas: [
      "Cerebellar Cortex",
      "Deep Cerebellar Nuclei",
      "Vermis",
      "Hemispheres",
    ],
    references: ["Ito, 2008", "Strick et al., 2009"],
    cameraPosition: { x: -1.703, y: -2.429, z: 3.315 },
  },
};

const hoverObjects = [];

// Loading bar elements
const loadingContainer = document.getElementById("loading-container");
const loadingProgress = document.getElementById("loading-progress");
const loadingPercentage = document.getElementById("loading-percentage");

// Function to update loading progress
function updateLoadingProgress(progress) {
  const percentage = Math.round(progress * 100);
  loadingProgress.style.width = `${percentage}%`;
  loadingPercentage.textContent = `${percentage}%`;
}

// Function to hide loading bar
function hideLoadingBar() {
  loadingContainer.style.opacity = "0";
  setTimeout(() => {
    loadingContainer.style.display = "none";
  }, 300);
}

gltfLoader.load(
  "./models/brain.glb",
  (gltf) => {
    const model = gltf.scene;
    model.scale.set(0.05, 0.05, 0.05);
    model.traverse((child) => {
      if (child.isMesh) {
        child.material = material;
        if (hoverObjectNames.includes(child.name)) {
          hoverObjects.push(child);
        }
      }
    });
    scene.add(model);

    // Hide loading bar when model is loaded
    hideLoadingBar();
  },
  (progress) => {
    // Update progress during loading
    const progressPercent = Math.min(progress.loaded / progress.total, 1);
    updateLoadingProgress(progressPercent);
  },
  (error) => {
    console.error("Error loading model:", error);
    // Hide loading bar even on error
    hideLoadingBar();
  }
);

// Ambient Light
const ambientLight = new THREE.AmbientLight(0xffffff, 1);
scene.add(ambientLight);

// Directional Lights
const directionalLights = [
  {
    color: 0xffffff,
    position: new THREE.Vector3(2, 2, 2),
  },
  {
    color: 0xffffff,
    position: new THREE.Vector3(-2, 2, -2),
  },
  {
    color: 0xffffff,
    position: new THREE.Vector3(2, 2, -2),
  },
  {
    color: 0xffffff,
    position: new THREE.Vector3(-2, 2, 2),
  },
];
const directionalLightsFolder = pane.addFolder({
  title: "Directional Lights",
  expanded: true,
});
directionalLights.forEach((light, index) => {
  const directionalLight = new THREE.DirectionalLight(light.color, 1);
  directionalLight.position.set(
    light.position.x,
    light.position.y,
    light.position.z
  );
  scene.add(directionalLight);
  const directionalLightHelper = new THREE.DirectionalLightHelper(
    directionalLight
  );
  scene.add(directionalLightHelper);
  directionalLightHelper.visible = false;
  const directionalLightFolder = directionalLightsFolder.addFolder({
    title: `Light ${index + 1}`,
    expanded: true,
  });
  directionalLightFolder.addBinding(directionalLightHelper, "visible", {
    label: `Helper`,
  });
  directionalLightFolder
    .addBinding(directionalLight, "position", {
      label: `Position`,
    })
    .on("change", (ev) => {
      directionalLight.position.set(ev.value.x, ev.value.y, ev.value.z);
      directionalLightHelper.update();
    });
});

// Raycaster
const raycaster = new THREE.Raycaster();

// Sizes
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

// Camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100
);
camera.position.x = 3.5;
camera.position.y = 0;
camera.position.z = 2.75;
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.enablePan = false;
controls.minDistance = 2;
controls.maxDistance = 8;

// Renderer
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.render(scene, camera);

// Handle Resize
window.addEventListener("resize", () => {
  sizes.width = window.innerWidth * currentMultiplier;
  sizes.height = window.innerHeight * currentHeightMultiplier;

  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  hideTooltip();
  resizeRendererOpen();
});

// Mouse
let haveMoved = false;
const mouse = new THREE.Vector2();
let clickStart = false;
const mouseDownPosition = new THREE.Vector2();
let clickMoved = false;
const DISTANCE_THRESHOLD = 10;

function updateMousePosition(clientX, clientY) {
  const windowWidth = sizes.width / currentMultiplier;
  const windowDiff = sizes.width - windowWidth;
  mouse.x = ((clientX + windowDiff) / sizes.width) * 2 - 1;
  mouse.y = -(clientY / sizes.height) * 2 + 1;
}
function updateClickMoved(clientX, clientY) {
  if (clickStart && !clickMoved) {
    const deltaX = Math.abs(clientX - mouseDownPosition.x);
    const deltaY = Math.abs(clientY - mouseDownPosition.y);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    if (distance > DISTANCE_THRESHOLD) {
      clickMoved = true;
    }
  }
}
function moveEvent(clientX, clientY) {
  haveMoved = true;
  updateMousePosition(clientX, clientY);
  if (tooltip.style.opacity === "1") {
    tooltip.style.left = clientX + "px";
    tooltip.style.top = clientY + "px";
  }
  updateClickMoved(clientX, clientY);
}
window.addEventListener("mousemove", (event) => {
  const clientX = event.clientX;
  const clientY = event.clientY;
  moveEvent(clientX, clientY);
});
window.addEventListener("touchmove", (event) => {
  const touch = event.touches[0];
  const clientX = touch.clientX;
  const clientY = touch.clientY;
  updateClickMoved(clientX, clientY);
});
function clickEvent() {
  haveMoved = false;
  clickStart = false;
  if (currentIntersect !== null && !clickMoved) {
    if (selectedObject !== null) {
      selectedObject.material = material;
    } else {
      regionInfoOpen();
    }
    selectedObject = currentIntersect;
    selectedObject.material = selectedMaterial;

    // Hide the instruction text when a region is selected
    const instructionText = document.getElementById("instruction-text");
    if (instructionText) {
      instructionText.style.display = "none";
    }

    showRegionInfo(currentIntersect.name);

    animateCameraToRegion(currentIntersect.name);
  }
}
window.addEventListener("click", () => {
  clickEvent();
});
window.addEventListener("touchend", (event) => {
  raycast(true);
  clickEvent();
});
function mouseDownEvent(clientX, clientY) {
  clickStart = true;
  clickMoved = false;
  mouseDownPosition.set(clientX, clientY);
}
window.addEventListener("mousedown", (event) => {
  const clientX = event.clientX;
  const clientY = event.clientY;
  mouseDownEvent(clientX, clientY);
});
window.addEventListener("touchstart", (event) => {
  const touch = event.touches[0];
  const clientX = touch.clientX;
  const clientY = touch.clientY;
  updateMousePosition(clientX, clientY);
  mouseDownEvent(clientX, clientY);
});

const regionInfoElement = document.getElementById("region-info");
const regionNameElement = document.getElementById("region-name");
const regionSubtitleElement = document.getElementById("region-subtitle");
const regionDescriptionElement = document.getElementById("region-description");
const regionFunctionsElement = document.getElementById("region-functions");
const regionKeyAreasElement = document.getElementById("region-key-areas");
const regionReferencesElement = document.getElementById("region-references");

function showRegionInfo(regionName) {
  regionNameElement.textContent = regionData[regionName].name;
  regionSubtitleElement.textContent = regionData[regionName].subtitle;
  regionDescriptionElement.textContent = regionData[regionName].description;
  regionFunctionsElement.innerHTML = regionData[regionName].functions
    .map((func) => `<span>${func}</span>`)
    .join("");
  regionKeyAreasElement.innerHTML = regionData[regionName].keyAreas
    .map((area) => `<li>• ${area}</li>`)
    .join("");
  regionReferencesElement.innerHTML = regionData[regionName].references
    .map((ref) => `<li>${ref}</li>`)
    .join("");
  if (isClosingTimeout) {
    clearTimeout(isClosingTimeout);
    isClosingTimeout = null;
  }
  regionInfoElement.style.display = "flex";
  regionInfoElement.style.opacity = "0.75";
}

function changeRendererWidth(multiplier) {
  sizes.width = window.innerWidth * multiplier;

  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
}

function changeRendererHeight(multiplier) {
  sizes.height = window.innerHeight * multiplier;

  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
}

// Tooltip
const tooltip = document.getElementById("tooltip");
const tooltipText = document.getElementById("tooltip-text");

function showTooltip(regionName) {
  tooltip.style.opacity = "1";
  tooltipText.textContent = regionName;
}

function hideTooltip() {
  tooltip.style.opacity = "0";
}

let selectedObject = null;
let currentIntersect = null;

// Close button functionality
let isClosingTimeout = null;
document.addEventListener("DOMContentLoaded", () => {
  const closeButton = document.getElementById("close-region-info");
  const regionInfo = document.getElementById("region-info");

  if (closeButton && regionInfo) {
    function closeRegionInfo() {
      regionInfo.style.opacity = "0";

      isClosingTimeout = setTimeout(() => {
        regionInfo.style.display = "none";
        isClosingTimeout = null;
      }, 700);

      // Reset selected object material
      if (selectedObject) {
        selectedObject.material = material;
        selectedObject = null;
      }

      // Show instruction text again
      const instructionText = document.getElementById("instruction-text");
      if (instructionText) {
        instructionText.style.display = "block";
      }

      regionInfoClose();
    }
    closeButton.addEventListener("click", () => {
      closeRegionInfo();
    });
    closeButton.addEventListener("touchend", (event) => {
      closeRegionInfo();
    });
  }
});

// Animate Renderer Width
const animateRendererTime = 0.7;
let animateRendererWidthStartTime = null;
let animateRendererWidthMultiplier = 1;
let currentMultiplier = 1;
let startMultiplier = 1;

let animateRendererHeightStartTime = null;
let animateRendererHeightMultiplier = 1;
let currentHeightMultiplier = 1;
let startHeightMultiplier = 1;

function animateRendererWidth(multiplier) {
  animateRendererWidthStartTime = timer.getElapsedTime();
  startMultiplier = currentMultiplier;
  animateRendererWidthMultiplier = multiplier;
}
function animateRendererHeight(multiplier) {
  animateRendererHeightStartTime = timer.getElapsedTime();
  startHeightMultiplier = currentHeightMultiplier;
  animateRendererHeightMultiplier = multiplier;
}
function regionInfoOpen() {
  if (window.innerHeight > window.innerWidth) {
    animateRendererHeight(0.7);
  } else {
    animateRendererWidth(1.3);
  }
}
function regionInfoClose() {
  if (currentHeightMultiplier !== 1) {
    animateRendererHeight(1);
  }
  if (currentMultiplier !== 1) {
    animateRendererWidth(1);
  }
}
function resizeRendererOpen() {
  if (currentHeightMultiplier !== 1 || currentMultiplier !== 1) {
    if (
      currentHeightMultiplier !== 1 &&
      window.innerHeight < window.innerWidth
    ) {
      animateRendererHeight(1);
      animateRendererWidth(1.3);
    } else if (
      currentMultiplier !== 1 &&
      window.innerHeight > window.innerWidth
    ) {
      animateRendererWidth(1);
      animateRendererHeight(0.7);
    }
  }
}

// FPS Counter
let frameCount = 0;
let lastTime = 0;
let fps = 0;
const fpsValueElement = document.getElementById("fps-value");
const fpsElement = document.getElementById("fps-display");
let isFpsVisible = false;
window.addEventListener("keydown", (event) => {
  if (event.key === "f") {
    if (!isFpsVisible) {
      fpsElement.style.display = "block";
      isFpsVisible = true;
    } else {
      fpsElement.style.display = "none";
      isFpsVisible = false;
    }
  }
});

// Raycasting
function raycast(mobile = false) {
  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects(hoverObjects);
  if (intersects.length > 0) {
    const auxIntersect = intersects[0].object;
    if (auxIntersect !== currentIntersect) {
      if (currentIntersect !== null && currentIntersect !== selectedObject) {
        currentIntersect.material = material;
      }
      currentIntersect = auxIntersect;
      if (currentIntersect !== selectedObject) {
        if (!mobile) {
          currentIntersect.material = hoverMaterial;
        }
      }
      const regionName = regionData[currentIntersect.name].name;
      if (!mobile) {
        showTooltip(regionName);
      }
    }
  } else {
    if (currentIntersect !== null) {
      if (currentIntersect !== selectedObject) {
        currentIntersect.material = material;
      }
      hideTooltip();
    }
    currentIntersect = null;
  }
}

// Camera animation
const animateCameraTime = 0.7;
let animateCameraStartTime = null;
let startCameraPosition = new THREE.Vector3();
let targetCameraPosition = new THREE.Vector3();
let isAnimatingCamera = false;
const CENTER_POINT = new THREE.Vector3(0, 0, 0);

function animateCameraToRegion(regionName) {
  // Store current camera position as starting point
  startCameraPosition.copy(camera.position);

  const regionCam = regionData[regionName].cameraPosition;
  const candidate1 = new THREE.Vector3(regionCam.x, regionCam.y, regionCam.z);
  const candidate2 = new THREE.Vector3(regionCam.x, regionCam.y, -regionCam.z);

  // Choose whichever is closer to the current camera position
  const dist1 = startCameraPosition.distanceTo(candidate1);
  const dist2 = startCameraPosition.distanceTo(candidate2);
  const chosenTarget = dist1 <= dist2 ? candidate1 : candidate2;

  // Set target position
  targetCameraPosition.copy(chosenTarget);

  // Start animation
  animateCameraStartTime = timer.getElapsedTime();
  isAnimatingCamera = true;
}

function getArcPoint(p1, p2, center, t) {
  // Always return exact endpoints at 0 and 1 to avoid micro jumps
  if (t <= 0) return p1.clone();
  if (t >= 1) return p2.clone();

  // Vectors from center
  const v1 = new THREE.Vector3().subVectors(p1, center);
  const v2 = new THREE.Vector3().subVectors(p2, center);

  // Normal of the plane (defines rotation plane)
  const n = new THREE.Vector3().crossVectors(v1, v2).normalize();
  const u = v1.clone().normalize();
  const v = new THREE.Vector3().crossVectors(n, u).normalize();

  // Signed angle between v1 and v2
  let angle = Math.acos(
    THREE.MathUtils.clamp(
      v1.clone().normalize().dot(v2.clone().normalize()),
      -1,
      1
    )
  );
  if (v1.clone().cross(v2).dot(n) < 0) angle = -angle;

  // Interpolate the distance from center (so it can zoom in/out)
  const radius = THREE.MathUtils.lerp(v1.length(), v2.length(), t);

  // Rotate along the arc by theta
  const theta = angle * t;

  const point = new THREE.Vector3()
    .addScaledVector(u, Math.cos(theta) * radius)
    .addScaledVector(v, Math.sin(theta) * radius)
    .add(center);

  return point;
}

// Animate
const timer = new THREE.Clock();
function tick() {
  // Timer
  const elapsedTime = timer.getElapsedTime();

  // FPS calculation
  if (isFpsVisible) {
    frameCount++;
    const currentTime = performance.now();
    if (currentTime - lastTime >= 1000) {
      // Update every second
      fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
      if (fpsValueElement) {
        fpsValueElement.textContent = fps;
      }
      frameCount = 0;
      lastTime = currentTime;
    }
  }

  // Raycasting material change
  if (haveMoved) {
    raycast();
  }

  // Animate renderer width
  if (animateRendererWidthStartTime !== null) {
    const progress = Math.min(
      (elapsedTime - animateRendererWidthStartTime) / animateRendererTime,
      1
    );
    currentMultiplier =
      startMultiplier +
      (animateRendererWidthMultiplier - startMultiplier) * progress;
    changeRendererWidth(currentMultiplier);
    if (elapsedTime - animateRendererWidthStartTime > animateRendererTime) {
      currentMultiplier = animateRendererWidthMultiplier;
      animateRendererWidthStartTime = null;
    }
  }

  // Animate renderer height
  if (animateRendererHeightStartTime !== null) {
    const progress = Math.min(
      (elapsedTime - animateRendererHeightStartTime) / animateRendererTime,
      1
    );
    currentHeightMultiplier =
      startHeightMultiplier +
      (animateRendererHeightMultiplier - startHeightMultiplier) * progress;
    changeRendererHeight(currentHeightMultiplier);
    if (elapsedTime - animateRendererHeightStartTime > animateRendererTime) {
      currentHeightMultiplier = animateRendererHeightMultiplier;
      animateRendererHeightStartTime = null;
    }
  }

  // Animate camera position
  if (isAnimatingCamera && animateCameraStartTime !== null) {
    const progress = Math.min(
      (elapsedTime - animateCameraStartTime) / animateCameraTime,
      1
    );

    // Interpolate camera position
    const arcPos = getArcPoint(
      startCameraPosition,
      targetCameraPosition,
      CENTER_POINT,
      progress
    );
    camera.position.copy(arcPos);

    if (elapsedTime - animateCameraStartTime > animateCameraTime) {
      // Animation complete
      camera.position.copy(targetCameraPosition);
      isAnimatingCamera = false;
      animateCameraStartTime = null;
    }
  }

  // Update controls
  controls.update();

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
}
tick();
